import { FC } from 'react';
import { AllieButton, AllieChipColorProps, AllieText } from '../../../components';
import {
  AllieArrowDownIcon,
  AllieArrowUpIcon,
  AllieBarChartOutlineIcon,
  AllieDotIcon,
  AlliePinUnmarked,
  AllieTachometerIcon,
  AllieTargetIcon,
  AllieTimeQuarterPastIcon,
} from '../../../styling/icons';
import {
  AllieChipStyled,
  CardContent,
  HeaderCard,
  InfoOP,
  MobileActionButtonsContainer,
  ProductionOrderStyled,
  StyledDescText,
  StyledMainText,
  TitleCard,
  VelocityChip,
  VelocityContainer,
  VelocityContent,
} from './ProductionOrder.styles';
import { playCircleOutline } from 'ionicons/icons';
import { ProductionOrderProps } from './ProductionOrder.types';
import { AllieCollapsibleCard } from '../CollapsibleCard';
import { IconData } from './components/IconData';
import { isTablet } from '../../../common/utils';
import { OrderStatus, ToolbarButtons } from './components/ToolbarButtons';
import { AllieColors } from '../../../common/color.types';

export const ProductionOrderV2: FC<ProductionOrderProps> = ({
  labels,
  orderId,
  startDate,
  productId,
  expectedSpeedValue,
  velocity,
  velocityVariation,
  velocityUnit,
  status,
  isCurrentShift,
  produced,
  accumulatedProduced,
  estimated,
  unit,
  onClickStartOp,
  onClickCloseOp,
  onClickPauseOp,
  onClickResumeOp,
  onClickSeeAllPOs,
  onClickEditOp,
  disabledSeeAllPOs,
  showDrawerButton = true,
  showActionButtons = true,
  isMobile = false,
  isNonWorkingShift = false,
}) => {
  const mapVelocityVariationChipColor: Record<'up' | 'down', AllieChipColorProps> = {
    up: {
      allieColor: 'success-main',
      backgroundColor: 'success-10',
    },
    down: {
      allieColor: 'error-70',
      backgroundColor: 'error-10',
    },
  };

  const mapVelocityVariationChipBorder: Record<'up' | 'down', string> = {
    up: '1px solid #ABEFC6',
    down: '1px solid var(--allie-color-error-20)',
  };

  const mapProductionStatusChipColor: Record<string, AllieColors> = {
    in_progress: 'neutral-90',
    done: 'neutral-70',
    paused: 'neutral-70',
  };
  const mapStatusLabel: Record<string, string> = {
    in_progress: labels.status,
    done: labels.statusDone,
    paused: labels.statusPaused,
  };

  return (
    <AllieCollapsibleCard title={labels.cardTitle} isCollapsible={isMobile} initialState={true}>
      <ProductionOrderStyled style={{ padding: isMobile ? '0 12px 12px' : '16px' }}>
        <HeaderCard>
          <TitleCard isMobile={isMobile}>
            <AllieText color="neutral-90" type="body1" weight={500} style={{ flexShrink: 0 }}>
              {`${labels.productionOrder} ${orderId ?? ' -'}`}
            </AllieText>
            {productId && (
              <AllieText color="neutral-90" type="body3" weight={400} lineClamp={1}>
                {`| ${productId}`}
              </AllieText>
            )}
            {status && (
              <AllieChipStyled status={status} style={{ flexShrink: 0 }}>
                {status === OrderStatus.InProgress && <AllieDotIcon />}
                <StyledDescText
                  type="body2"
                  color={mapProductionStatusChipColor[status]}
                  weight={500}
                >
                  {mapStatusLabel[status]}
                </StyledDescText>
              </AllieChipStyled>
            )}
          </TitleCard>
          {showActionButtons && !isTablet && (
            <ToolbarButtons
              labels={labels}
              orderId={orderId}
              status={status}
              onClickStartOp={onClickStartOp}
              playCircleOutline={playCircleOutline}
              isCurrentShift={isCurrentShift}
              onClickCloseOp={onClickCloseOp}
              onClickPauseOp={onClickPauseOp}
              onClickResumeOp={onClickResumeOp}
              showDrawerButton={showDrawerButton}
              onClickSeeAllPOs={onClickSeeAllPOs}
              disabledSeeAllPOs={disabledSeeAllPOs}
              onClickEditOp={onClickEditOp}
            />
          )}
        </HeaderCard>
        <CardContent status={status}>
          <InfoOP isMobile={isMobile}>
            <IconData
              text={labels.begin}
              icon={<AllieTimeQuarterPastIcon display="contents" />}
              value={startDate || '-'}
            />
            <IconData
              text={labels.producedOPShift}
              icon={<AlliePinUnmarked display="contents" />}
              value={produced ? `${produced} ${unit}` : '-'}
            />
          </InfoOP>
          <InfoOP isMobile={isMobile}>
            <IconData
              text={labels.accumulatedProductionShift}
              icon={
                <AllieBarChartOutlineIcon display="contents" style={{ transform: 'scaleX(-1)' }} />
              }
              value={accumulatedProduced ? `${accumulatedProduced} ${unit}` : '-'}
            />
            <IconData
              text={labels.estimatedOPShift}
              icon={<AllieTargetIcon display="contents" allieColor="neutral-70" />}
              value={estimated ? `${estimated} ${unit}` : '-'}
            />
          </InfoOP>

          <VelocityContainer isMobile={isMobile}>
            <VelocityContent>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ height: 14 }}>
                  <AllieTachometerIcon color="neutral-60" />
                </div>
                <StyledDescText type="body3" color="neutral-60">
                  {labels.velocity}
                </StyledDescText>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledMainText color="neutral-90" type="body1" weight={500}>
                  {velocity ? `${velocity} ${velocityUnit}` : '----'}
                </StyledMainText>
                {velocity && velocityVariation && velocityVariation !== 0 && (
                  <VelocityChip
                    color={
                      velocityVariation > 0
                        ? mapVelocityVariationChipColor['up']
                        : mapVelocityVariationChipColor['down']
                    }
                    style={{
                      height: 'auto',
                      border:
                        velocityVariation > 0
                          ? mapVelocityVariationChipBorder['up']
                          : mapVelocityVariationChipBorder['down'],
                    }}
                  >
                    {velocityVariation <= 0 ? (
                      <div style={{ height: 17, marginRight: 4 }}>
                        <AllieArrowDownIcon />
                      </div>
                    ) : (
                      <div style={{ height: 17, marginRight: 4 }}>
                        <AllieArrowUpIcon />
                      </div>
                    )}
                    <AllieText
                      type="body3"
                      color={velocityVariation > 0 ? 'success-70' : 'error-70'}
                      weight={500}
                    >
                      {velocityVariation} %
                    </AllieText>
                  </VelocityChip>
                )}
              </div>
            </VelocityContent>
            {isMobile ? (
              <div
                style={{
                  borderBottom: '1px solid var(--allie-color-neutral-30)',
                  width: '98%',
                  margin: 'var(--allie-spacing-lg) 0',
                }}
              />
            ) : (
              <div style={{ borderLeft: '1px solid var(--allie-color-neutral-30)', height: 54 }} />
            )}
            <VelocityContent>
              <div style={{ display: 'flex', gap: '8px' }}>
                <div style={{ height: 14 }}>
                  <AllieTachometerIcon color="neutral-60" />
                </div>
                <StyledDescText type="body3" color="neutral-60">
                  {labels.expectedSpeed}
                </StyledDescText>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <StyledMainText color="neutral-90" type="body1" weight={500}>
                  {isNonWorkingShift || !expectedSpeedValue
                    ? '----'
                    : `${expectedSpeedValue} ${velocityUnit}`
                  }
                </StyledMainText>
              </div>
            </VelocityContent>
          </VelocityContainer>
        </CardContent>
        {isMobile && (
          <MobileActionButtonsContainer>
            {showDrawerButton && (
              <AllieButton
                allieSize="small"
                style={
                  {
                    '--border-color': 'var(--allie-color-neutral-40)',
                  } as React.CSSProperties
                }
                fill="outline"
                full
                allieType="secondary"
                allieColor="neutral"
                corner="rounded-edges-plus"
                onClick={() => !disabledSeeAllPOs && onClickSeeAllPOs()}
                disabled={disabledSeeAllPOs}
              >
                {labels.seeOpButton}
              </AllieButton>
            )}
          </MobileActionButtonsContainer>
        )}
        {showActionButtons && isTablet && (
          <ToolbarButtons
            labels={labels}
            orderId={orderId}
            status={status}
            onClickStartOp={onClickStartOp}
            playCircleOutline={playCircleOutline}
            isCurrentShift={isCurrentShift}
            onClickCloseOp={onClickCloseOp}
            onClickPauseOp={onClickPauseOp}
            onClickResumeOp={onClickResumeOp}
            showDrawerButton={showDrawerButton}
            onClickSeeAllPOs={onClickSeeAllPOs}
            disabledSeeAllPOs={disabledSeeAllPOs}
            onClickEditOp={onClickEditOp}
          />
        )}
      </ProductionOrderStyled>
    </AllieCollapsibleCard>
  );
};
